import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, ExternalLink, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentViewer({ fileUrl, fileName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  useEffect(() => {
    const loadFile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const fullUrl = `${BACKEND_URL}${fileUrl}`;
        const token = localStorage.getItem('token');
        
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.status}`);
        }
        
        const contentType = response.headers.get('content-type') || '';
        setFileType(contentType);
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('Error loading file:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    loadFile();
    
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileUrl]);

  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      // Create a new window with the blob content
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        if (fileType?.includes('pdf')) {
          newWindow.document.write(`
            <html>
              <head><title>${fileName || 'Document'}</title></head>
              <body style="margin:0;padding:0;">
                <embed src="${blobUrl}" type="application/pdf" width="100%" height="100%" />
              </body>
            </html>
          `);
        } else if (fileType?.includes('image')) {
          newWindow.document.write(`
            <html>
              <head><title>${fileName || 'Document'}</title></head>
              <body style="margin:0;padding:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#1a1a1a;">
                <img src="${blobUrl}" style="max-width:100%;max-height:100vh;" />
              </body>
            </html>
          `);
        } else {
          newWindow.location.href = blobUrl;
        }
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-96">
          <Loader2 size={48} className="animate-spin text-teal-500 mb-4" />
          <p className="text-slate-600">Loading document...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle size={48} className="text-red-500 mb-4" />
          <p className="text-red-600 font-medium mb-2">Failed to load document</p>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      );
    }

    if (!blobUrl) return null;

    // PDF viewer
    if (fileType?.includes('pdf')) {
      return (
        <iframe
          src={blobUrl}
          className="w-full h-[70vh] border-0"
          title={fileName || 'Document'}
        />
      );
    }

    // Image viewer
    if (fileType?.includes('image')) {
      return (
        <div className="flex items-center justify-center bg-slate-900 rounded-lg p-4">
          <img
            src={blobUrl}
            alt={fileName || 'Document'}
            className="max-w-full max-h-[70vh] object-contain"
          />
        </div>
      );
    }

    // Text file viewer
    if (fileType?.includes('text')) {
      return (
        <iframe
          src={blobUrl}
          className="w-full h-[70vh] border border-slate-200 rounded-lg bg-white"
          title={fileName || 'Document'}
        />
      );
    }

    // Generic fallback - download prompt
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-slate-50 rounded-lg">
        <p className="text-slate-600 mb-4">This file type cannot be previewed in the browser.</p>
        <button
          onClick={handleDownload}
          className="px-6 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 flex items-center gap-2"
        >
          <Download size={20} />
          Download File
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h2 className="font-semibold text-slate-800">{fileName || 'Document'}</h2>
            <p className="text-sm text-slate-500">{fileType || 'Unknown type'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenInNewTab}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink size={20} />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
              title="Download"
            >
              <Download size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
